import React, { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "../api/useApi";
import { frameThumbUrl, submitClientLabels, UploadFrameItem } from "../api/resources";
import { extractPoseFromImages } from "../pose/imagePose";
import { buildFrameKeyedPoseFileJson } from "../pose/clientPose";
import { MODEL_VERSION } from "../pose/mediapipeConfig";

/**
 * Labels an upload's frames in this browser, for a clip that has none.
 *
 * It runs over the SPLIT FRAMES rather than the video: they are the images the labeller and
 * renderer use, each carries a fileId so the result pairs exactly, and they need no seeking. The
 * result is submitted as a frame-keyed pose file; the labeller then reads it exactly as it reads an
 * upload-time one, so no labelling logic lives here.
 *
 * Deliberately just a caption: progress is shown by the processing card it sits in — the bar
 * (confirmed label orders over all) and the frame strip filling in as orders confirm.
 */
type Props = {
  uploadId: number;
  frames: UploadFrameItem[];
  onSubmitted: () => void;
  /** The upload's stored capture cadence (uploadFrames.captureFps): stamped on the pose file so the
   * value agrees with the frames it labels — 10 for golf, the native rate for movement sports. */
  captureFps: number;
};

export default function ClientLabellingStep({ uploadId, frames, onSubmitted, captureFps }: Props) {
  const apiFetch = useApi();
  const [done, setDone] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // One run per mount: React may render twice, and the extraction is expensive.
  const started = useRef(false);

  const run = useCallback(async () => {
    setError(null);
    setDone(0);
    try {
      const result = await extractPoseFromImages(
        frames.map((f) => ({ fileId: f.fileId, url: frameThumbUrl(f.fileId) })),
        (completed) => setDone(completed)
      );
      if (result.frames.length === 0) {
        throw new Error("no frames could be read");
      }
      const poseFile = buildFrameKeyedPoseFileJson(result.frames, {
        captureWidth: result.captureWidth,
        captureHeight: result.captureHeight,
        modelVersion: MODEL_VERSION,
        fps: captureFps,
      });
      const res = await submitClientLabels(apiFetch, uploadId, poseFile);
      if (res?.func_status !== 1) throw new Error("the server rejected the labels");
      onSubmitted();
    } catch (e: any) {
      setError(e?.message ?? "Labelling failed on this device.");
    }
  }, [apiFetch, frames, uploadId, onSubmitted, captureFps]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void run();
  }, [run]);

  if (error) {
    return (
      <div className="text-xs text-slate-500" data-testid="client-labelling-error">
        Couldn't analyse this clip on your device ({error}).{" "}
        <button type="button" onClick={() => void run()} className="text-el-700 underline">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="text-xs text-slate-500" data-testid="client-labelling-step">
      Analysing on this device · {done} / {frames.length} frames
    </div>
  );
}
